import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Button, TextInput } from '@/components';
import { AuthLayout } from '@/features/auth/components/AuthLayout';
import { useAuth } from '@/features/auth/context/AuthContext';
import { PASSWORD_PATTERN, PASSWORD_PATTERN_MESSAGE } from '@/utils/constants';
import toast from 'react-hot-toast';

const signupSchema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Invalid email address').required('Email is required'),
  password: yup
    .string()
    .matches(PASSWORD_PATTERN, PASSWORD_PATTERN_MESSAGE)
    .required('Password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
  companyName: yup.string().required('Company name is required'),
  websiteUrl: yup.string().url('Invalid URL format').required('Website is required'),
});

type SignupFormData = yup.InferType<typeof signupSchema>;

export const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: yupResolver(signupSchema) as any,
  });

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    try {
      await signup({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        companyName: data.companyName,
        websiteUrl: data.websiteUrl,
      });
      toast.success('Account created! Please check your email to verify your account.');
      navigate('/verify-email', { state: { email: data.email } });
    } catch (error: any) {
      toast.error(error || 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="space-y-8">
        <div className="text-center space-y-2">
          <h1 className="text-2xl sm:text-3xl font-bold">Create an account</h1>
          <p className="text-muted-foreground">Get started with Postpally today</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextInput
              label="First Name"
              placeholder="John"
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              {...register('firstName')}
            />
            <TextInput
              label="Last Name"
              placeholder="Doe"
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              {...register('lastName')}
            />
          </div>

          <TextInput
            label="Email"
            type="email"
            placeholder="john@example.com"
            error={!!errors.email}
            helperText={errors.email?.message}
            {...register('email')}
          />

          <TextInput
            label="Company Name"
            placeholder="Acme Inc."
            error={!!errors.companyName}
            helperText={errors.companyName?.message}
            {...register('companyName')}
          />

          <TextInput
            label="Website URL"
            type="url"
            placeholder="https://www.example.com"
            error={!!errors.websiteUrl}
            helperText={errors.websiteUrl?.message}
            {...register('websiteUrl')}
          />

          <TextInput
            label="Password"
            type="password"
            placeholder="Create a password"
            showPasswordToggle
            error={!!errors.password}
            helperText={errors.password?.message}
            {...register('password')}
          />

          <TextInput
            label="Confirm Password"
            type="password"
            placeholder="Confirm your password"
            showPasswordToggle
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Creating account...' : 'Create account'}
          </Button>
        </form>

        <div className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link to="/login" className="text-primary hover:underline font-medium">
            Sign in
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
};
